import { sendBugsnagError } from "@utils/Bugsnag";
import { stripNonDigits, removeEmpty, trimObject } from "@utils";
import { errorResponseMessage } from "./errorResponseMessage";
import { isSchemaError } from "./isSchemaError";
import { defaultAction } from "./defaultAction";
import { OPEN_ERROR_BOX, SET_PHONE } from "@constants/actionTypes";
import { getCustomerProfile } from "./getCustomerProfile";

export const updateCustomerPhoneAndAddress =
  (phone, address) => async (dispatch, getState, api) => {
    try {
      const { customerInfo } = getState();
      const customerAddressExists = ["street", "city", "state", "zip"].every(
        value => !!customerInfo[value]
      );
      const phoneExists = customerInfo.phone && customerInfo.phone !== "0";

      if (customerInfo.missingInfo) {
        await Promise.all([
          customerAddressExists || dispatch(updateCustomerAddress(address)),
          phoneExists || dispatch(updateCustomerPhone(phone))
        ]);
      }
    } catch (error) {
      sendBugsnagError(error, "actions/updateCustomerPhoneAndAddress");
      throw `updateCustomerPhoneAndAddress: ${error}`;
    }
  };

export const updateCustomerAddress =
  (address, makeDefault = false) =>
  async (dispatch, getState, api) => {
    try {
      const { id } = getState().customerInfo;
      await api.post("v3/customers/addresses", {
        customerId: id,
        address,
        makeDefault
      });
      return "success";
    } catch (error) {
      sendBugsnagError(error, "actions/updateCustomerAddress");
      let errorMessage = errorResponseMessage(error);
      if (!errorMessage || isSchemaError(errorMessage)) {
        errorMessage =
          "Something went wrong, please check your information and try again.";
      }
      return { error: errorMessage };
    }
  };

export const updateCustomerPhone =
  (phone, isDefault, resetState) => async (dispatch, getState, api) => {
    try {
      const { id } = getState().customerInfo;
      const postObject = isDefault
        ? {
            customerId: id,
            phone: { number: stripNonDigits(phone) },
            isDefault: true
          }
        : {
            customerId: id,
            phone: { number: stripNonDigits(phone) }
          };
      await api.post("v3/customers/phones", postObject);
      if (resetState) {
        await dispatch(getCustomerProfile());
      }
      return "success";
    } catch (error) {
      sendBugsnagError(error, "actions/updateCustomerPhone");
      dispatch(
        defaultAction(
          OPEN_ERROR_BOX,
          "We are sorry. Please check if your information is correct or refresh the page and try again. Thank you."
        )
      );
    }
  };

export const updateCustomerProfile =
  editCustomerInfo => async (dispatch, getState, api) => {
    try {
      await api.put("v3/customers", {
        firstname: editCustomerInfo.firstname,
        lastname: editCustomerInfo.lastname
      });
      dispatch(getCustomerProfile());
    } catch (error) {
      sendBugsnagError(error, "actions/updateCustomerProfile/customers");
      let message = errorResponseMessage(error);
      if (isSchemaError(message)) {
        message = "Something went wrong, please refresh the page.";
      }
      return { error: message };
    }
    try {
      await api.post("v3/customers/phones", {
        customerId: editCustomerInfo.id,
        phone: { number: editCustomerInfo.phone },
        isDefault: true
      });
      dispatch(getCustomerProfile());
    } catch (error) {
      sendBugsnagError(error, "actions/updateCustomerProfile/phones");
      let message = errorResponseMessage(error);
      if (isSchemaError(message)) {
        message = "Something went wrong, please refresh the page.";
      }
      return { error: message };
    }

    try {
      await api.post("v3/customers/addresses", {
        customerId: editCustomerInfo.id,
        address: {
          firstname: editCustomerInfo.firstname,
          lastname: editCustomerInfo.lastname,
          street: editCustomerInfo.street,
          street2: editCustomerInfo.street2 || "",
          city: editCustomerInfo.city,
          state: editCustomerInfo.state,
          postcode: editCustomerInfo.zip
        },
        makeDefault: true
      });
      dispatch(getCustomerProfile());
      return "success";
    } catch (error) {
      sendBugsnagError(
        error,
        "actions/updateCustomerProfile/customers/addresses"
      );
      let message = errorResponseMessage(error);
      if (isSchemaError(message)) {
        message = "Something went wrong, please refresh the page.";
      }
      return { error: message };
    }
  };

export const postPrimaryAddress = address => async (dispatch, getState) => {
  const { customerInfo } = getState();
  let addressToPost = removeEmpty({ ...address });
  addressToPost = trimObject(addressToPost);
  if (
    customerInfo.firstname !== addressToPost.firstname ||
    customerInfo.lastname !== addressToPost.lastname ||
    customerInfo.state !== addressToPost.state ||
    customerInfo.street !== addressToPost.street ||
    customerInfo.city !== addressToPost.city ||
    customerInfo.zip !== addressToPost.zip ||
    (customerInfo.street2 && customerInfo.street2 !== addressToPost.street2) ||
    (addressToPost.street2 && addressToPost.street2 !== customerInfo.street2)
  ) {
    addressToPost.postcode = addressToPost.zip;

    delete addressToPost.zip;
    delete addressToPost.fullname;
    const addressPostResponse = await dispatch(
      updateCustomerAddress(addressToPost, true)
    );
    if (addressPostResponse === "success") {
      const customerGetResponse = await dispatch(getCustomerProfile());
      return customerGetResponse;
    } else {
      return addressPostResponse;
    }
  }
};

export const getStateAndCityFromZip =
  zip => async (dispatch, getState, api) => {
    try {
      const results = await api.get(`v3/customers/addresses/lookup?zip=${zip}`);
      return results.data;
    } catch (error) {
      sendBugsnagError(error, "actions/getStateAndCityFromZip");
    }
  };
